import React, { useMemo, useState } from 'react';
import {
  Box,
  Badge,
  Chip,
  Alert,
  Tooltip,
  Menu,
  Typography,
  IconButton,
  Stack,
  FormControlLabel,
  Checkbox,
  FormGroup,
  Button,
  TextField,
  InputAdornment,
} from '@mui/material';
import { getTranslation } from 'common';
import PriorityHighIcon from '@mui/icons-material/PriorityHigh';
import { LabelOutlined, Title } from '@mui/icons-material';
import FilterListIcon from '@mui/icons-material/FilterList';
import SortIcon from '@mui/icons-material/Sort';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import FilterListOffIcon from '@mui/icons-material/FilterListOff';
import {
  buildMyActivitiesFilterChips,
  CONSTANTS,
  SortCriterion,
} from '../common/utilities';
import { SortState } from '../hooks/useSortableList';
import { ProcessModelType } from '../model/processModel';
import { ServerTag, Task } from '../model/activitiesModel';
import { Priority } from '../hooks/usePriorities';
import MyActivitiesFilterMenu from './MyActivitiesFilterMenu';
import MyActivitiesSortMenu from './MyActivitiesSortMenu';
import MyActivitiesCardsViewMode from './MyActivitiesCardsViewMode';
import UserDataFilter from './UserDataFilter/UserDataFilter';
import { Organization } from 'model/organizationModel';
import { Translations } from 'model/messageModel';
import FlowentiButton from './flowenti-ui-kit/FlowentiButton';
import Labels from './labels/Labels';
import { Search } from '@mui/icons-material';
import { User } from './UserItem';


interface MyActivitiesFuncBarProps {
  liveProcessCanUse: boolean | undefined;
  bypassSubscription: boolean | undefined;
  organization: Organization;
  translations: Translations;
  completeLabel: boolean;
  appliedFilter: number;
  sortCriteria: SortCriterion;
  setStartProcess: (start: boolean) => void;
  navigate: (path: string, options: { state: any }) => void;
  setCompleteLabel: (value: boolean | ((prev: boolean) => boolean)) => void;
  searchText: string;
  setSearchText: (text: string) => void;
  searchTags: number[];
  setSearchTags: React.Dispatch<React.SetStateAction<number[]>>;
  selectedPriorities: string[];
  setSelectedPriorities: React.Dispatch<React.SetStateAction<string[]>>;
  selectedModels: number[];
  setSelectedModels: (models: number[]) => void;
  selectedActivities: string[];
  setSelectedActivities: (activities: string[]) => void;
  selectedUserDataKey: string | null;
  handleSelectUserDataKey: (key: string | null) => void;
  setSortCriteria: (criteria: any) => void;
  handleClearFilters: (disableLocalStorage?: boolean, clearUserData?: boolean) => void;
  processModels: ProcessModelType[];
  tasks: { todo: Task[], doing: Task[] };
  uniqueActivities: string[];
  displayedTags: ServerTag[];
  filteredTags: ServerTag[];
  showAllTags: boolean;
  setShowAllTags: React.Dispatch<React.SetStateAction<boolean>>;
  priorities: Priority[];
  userDataKeys: string[];
  SUPPORTED_SORTS: any[];
  view: 'minimal' | 'compact' | 'extended';
  handleViewChange: (newView: 'minimal' | 'compact' | 'extended') => void;
  selectedCreatorIds: number[];
  setSelectedCreatorIds: (ids: number[]) => void;
  orgUsers: User[];
}

const MyActivitiesFuncBar: React.FC<MyActivitiesFuncBarProps> = ({
  liveProcessCanUse,
  bypassSubscription,
  organization,
  translations,
  completeLabel,
  appliedFilter,
  sortCriteria,
  setStartProcess,
  navigate,
  setCompleteLabel,
  searchText,
  setSearchText,
  searchTags,
  setSearchTags,
  selectedPriorities,
  setSelectedPriorities,
  selectedModels,
  setSelectedModels,
  selectedActivities,
  setSelectedActivities,
  selectedUserDataKey,
  handleSelectUserDataKey,
  setSortCriteria,
  handleClearFilters,
  processModels,
  tasks,
  uniqueActivities,
  displayedTags,
  filteredTags,
  showAllTags,
  setShowAllTags,
  priorities,
  userDataKeys,
  SUPPORTED_SORTS,
  view,
  handleViewChange,
  selectedCreatorIds,
  setSelectedCreatorIds,
  orgUsers,
}) => {
  const [filterAnchorEl, setFilterAnchorEl] = useState<HTMLElement | null>(null);
  const [sortAnchorEl, setSortAnchorEl] = useState<HTMLElement | null>(null);
  const [userDataMenuAnchorEl, setUserDataMenuAnchorEl] = useState<HTMLElement | null>(null);
  const [labelsMenuAnchorEl, setLabelsMenuAnchorEl] = useState<HTMLElement | null>(null);
  const [prioritiesMenuAnchorEl, setPrioritiesMenuAnchorEl] = useState<HTMLElement | null>(null);
  const isSearchActive = Boolean(searchText?.trim());
  const [showSearchChip, setShowSearchChip] = useState(false);

  const handleFilterClick = (event: React.MouseEvent<HTMLElement>) => {
    setFilterAnchorEl(event.currentTarget);
  };

  const handleSortClick = (event: React.MouseEvent<HTMLElement>) => {
    setSortAnchorEl(event.currentTarget);
  };

  const handleUserDataClick = (event: React.MouseEvent<HTMLElement>) => {
    setUserDataMenuAnchorEl(event.currentTarget);
  };

  const handleLabelsClick = (event: React.MouseEvent<HTMLElement>) => {
    setLabelsMenuAnchorEl(event.currentTarget);
  };

  const handleCloseUserData = () => {
    setUserDataMenuAnchorEl(null);
  };

  const handleCloseSort = () => {
    setSortAnchorEl(null);
  };

  const handleCloseFilter = () => {
    setFilterAnchorEl(null);
  };

  const handleCloseLabels = () => {
    setLabelsMenuAnchorEl(null);
  };

  const handlePrioritiesClick = (event: React.MouseEvent<HTMLElement>) => {
    setPrioritiesMenuAnchorEl(event.currentTarget);
  };

  const handleClosePriorities = () => {
    setPrioritiesMenuAnchorEl(null);
  };

  const handlePriorityCheckboxChange = (priority: string) => {
    setSelectedPriorities((prev) =>
      prev.includes(priority)
        ? prev.filter((p) => p !== priority)
        : [...prev, priority]
    );
  };

  const handleLabelCheckboxChange = (tagId: number) => {
    setSearchTags((prev) =>
      prev.includes(tagId)
        ? prev.filter((id) => id !== tagId)
        : [...prev, tagId]
    );
  };

  const handleSortOptionClick = (field: string) => {
    setSortCriteria((prevState: any) => ({
      ...prevState,
      [field]:
        prevState[field] === SortState.Asc
          ? SortState.Desc
          : [SortState.None, undefined].includes(prevState[field])
            ? SortState.Asc
            : SortState.None,
    }));
  };

  const filterChips = useMemo(() => {
    return buildMyActivitiesFilterChips({
      searchText,
      showSearchChip,
      selectedModels,
      processModels,
      selectedActivities,
      selectedUserDataKey,
      searchTags,
      filteredTags,
      selectedPriorities,
      activityNoNameLabel: getTranslation(translations, "activity.no_name"),
      setSearchText,
      setShowSearchChip,
      setSelectedModels,
      setSelectedActivities,
      handleSelectUserDataKey,
      setSearchTags,
      setSelectedPriorities,
      selectedCreatorIds,
      setSelectedCreatorIds,
      orgUsers,
      translations,
    });
  }, [
    filteredTags,
    handleSelectUserDataKey,
    processModels,
    searchTags,
    searchText,
    selectedActivities,
    selectedModels,
    selectedPriorities,
    selectedUserDataKey,
    setSearchTags,
    setSearchText,
    setSelectedActivities,
    setSelectedModels,
    setSelectedPriorities,
    showSearchChip,
    translations,
    selectedCreatorIds,
    orgUsers,
  ]);

  return (
    <Stack>
      <Box
        id="utility-box"
        sx={{
          display: "grid",
          gridTemplateColumns: "auto 1fr auto auto",
          alignItems: "center",
          columnGap: 1,
          flex: 1,
          mt: 3,
        }}
      >
        <FlowentiButton
          id="startprocess-button"
          variant="contained"
          disabled={!bypassSubscription && !liveProcessCanUse}
          onClick={() => setStartProcess(true)}
          sx={{ mr: 16 }}
        >
          {getTranslation(
            translations,
            "pm.startprocess.button.ok.caption"
          )}
        </FlowentiButton>
        {liveProcessCanUse === false && !bypassSubscription && (<Box display="flex" alignItems="center" gap={1}>
          <Alert id="live-process-exceeded" sx={{ height: 40, alignItems: "center", overflowY: "hidden" }} severity="error">{getTranslation(translations, "live_processes.limit.reached")}
          </Alert>
          <FlowentiButton
            variant="outlined"
            size="small"
            onClick={() => navigate(`/dashboard/organizations/${organization.id}/billing`, { state: { organizationName: organization.name } })}
          >
            {getTranslation(translations, "generic.upgrade")}
          </FlowentiButton>
        </Box>
        )}
        <Box id="view-and-filter-otpions" sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <MyActivitiesCardsViewMode
            view={view}
            handleViewChange={handleViewChange}
            translations={translations}
          />
          <Badge
            anchorOrigin={{
              vertical: "top",
              horizontal: "left",
            }}
            color="primary"
            badgeContent={selectedUserDataKey ? 1 : 0}
          >
            <FlowentiButton
              id="userdata-menu"
              onClick={handleUserDataClick}
              variant="outlined"
              startIcon={<FormatListBulletedIcon color='primary' />}
            >
              {getTranslation(translations, "generic.userdata.caption")}
            </FlowentiButton>
          </Badge>
          <Badge
            anchorOrigin={{
              vertical: "top",
              horizontal: "left",
            }}
            color="primary"
            badgeContent={searchTags.length}
          >
            <Tooltip title={getTranslation(translations, "labels_view.tooltip")}>
              <FlowentiButton
                variant="outlined"
                onClick={handleLabelsClick}
                startIcon={completeLabel ? <Title color='primary' /> : <LabelOutlined color='primary' />}
              >
                {getTranslation(translations, "generic.labels.caption")}
              </FlowentiButton>
            </Tooltip>
          </Badge>
          <Badge
            anchorOrigin={{
              vertical: "top",
              horizontal: "left",
            }}
            color="primary"
            badgeContent={selectedPriorities.length}
          >
            <Tooltip title={getTranslation(translations, "generic.priorities.caption")}>
              <FlowentiButton
                variant="outlined"
                onClick={handlePrioritiesClick}
                startIcon={<PriorityHighIcon color='primary' />}
              >
                {getTranslation(translations, "generic.priorities.caption")}
              </FlowentiButton>
            </Tooltip>
          </Badge>
          <Badge
            anchorOrigin={{
              vertical: "top",
              horizontal: "left",
            }}
            color="primary"
            badgeContent={appliedFilter}
          >
            <FlowentiButton
              id="filter-button"
              onClick={handleFilterClick}
              startIcon={<FilterListIcon color='primary' />} variant="outlined"
            >
              {getTranslation(translations, "generic.filters.caption")}
            </FlowentiButton>
          </Badge>
          <Badge
            anchorOrigin={{
              vertical: "top",
              horizontal: "left",
            }}
            color="primary"
            badgeContent={
              Object.keys(sortCriteria).filter(
                (key) => sortCriteria[key] !== SortState.None
              ).length
            }
          >
            <FlowentiButton
              id="order-button"
              onClick={handleSortClick}
              startIcon={<SortIcon color='primary' />} variant="outlined"
            >
              {getTranslation(translations, "generic.sort.caption")}
            </FlowentiButton>
          </Badge>
          <TextField
            onKeyDown={(e) => e.stopPropagation()}
            id="search-tasks-text"
            variant="outlined"
            size="small"
            placeholder={getTranslation(translations, "pm.organizationchart.ricerca.placeholder")}
            sx={{
              width: 180,
              "& .MuiOutlinedInput-root": {
                borderRadius: "20px",
                height: 42,
                backgroundColor: isSearchActive ? "rgba(84, 189, 193, 0.12)" : "transparent",
                "& fieldset": {
                  borderColor: isSearchActive ? "primary.main" : undefined,
                  borderWidth: isSearchActive ? 2 : undefined,
                },
                "&:hover fieldset": {
                  borderColor: isSearchActive ? "primary.main" : undefined,
                },
                "&.Mui-focused fieldset": {
                  borderColor: "primary.main",
                },
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search color={isSearchActive ? "primary" : "inherit"} />
                </InputAdornment>
              ),
            }}
            value={searchText}
            onBlur={() => setShowSearchChip(Boolean(searchText.trim()))}
            onChange={(e) => {
              e.preventDefault();
              if (!e.target.value.trim()) {
                setShowSearchChip(false);
              }
              setSearchText(e.target.value);
            }}
          />
        </Box>
        {filterChips.length > 0 && (
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            flexWrap="wrap"
            useFlexGap
            sx={{
              maxWidth: 480,
              minWidth: 0,
            }}
          >
            {filterChips.map((chip, index) => (
              <Chip
                key={`${chip.label}-${index}`}
                label={chip.label}
                size="small"
                onDelete={chip.onDelete}
              />
            ))}
          </Stack>
        )}
      </Box>
      <MyActivitiesFilterMenu
        open={Boolean(filterAnchorEl)}
        anchorEl={filterAnchorEl}
        onClose={handleCloseFilter}
        translations={translations}
        handleClearFilters={handleClearFilters}
        processModels={processModels}
        selectedModels={selectedModels}
        setSelectedModels={setSelectedModels}
        tasks={tasks}
        uniqueActivities={uniqueActivities}
        selectedActivities={selectedActivities}
        setSelectedActivities={setSelectedActivities}
        userDataKeys={userDataKeys}
        selectedUserDataKey={selectedUserDataKey}
        handleSelectUserDataKey={handleSelectUserDataKey}
        selectedCreatorIds={selectedCreatorIds}
        setSelectedCreatorIds={setSelectedCreatorIds}
        orgUsers={orgUsers}
      />
      <MyActivitiesSortMenu
        open={Boolean(sortAnchorEl)}
        anchorEl={sortAnchorEl}
        onClose={handleCloseSort}
        sortCriteria={sortCriteria}
        SUPPORTED_SORTS={SUPPORTED_SORTS}
        selectedUserDataKey={selectedUserDataKey}
        handleSortOptionClick={handleSortOptionClick}
      />
      <Menu
        anchorEl={userDataMenuAnchorEl}
        open={Boolean(userDataMenuAnchorEl)}
        onClose={handleCloseUserData}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
      >
        <Box p={2} component={Stack} sx={{ minWidth: 200, overflowX: "hidden" }}>
          {userDataKeys.length > 0 && (
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                {getTranslation(translations, "generic.filter.by.userdata")}
              </Typography>
              <IconButton
                color="primary"
                onClick={() => {
                  handleSelectUserDataKey(null);
                  handleCloseUserData();
                }}
              >
                <FilterListOffIcon />
              </IconButton>
            </Box>
          )}
          <UserDataFilter
            availableKeys={userDataKeys}
            selectedKey={selectedUserDataKey}
            onSelectKey={handleSelectUserDataKey}
          />
        </Box>
      </Menu>
      <Menu
        anchorEl={labelsMenuAnchorEl}
        open={Boolean(labelsMenuAnchorEl)}
        onClose={handleCloseLabels}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
      >
        <Box p={2} component={Stack} sx={{ minWidth: 250, maxWidth: 300, overflowX: "hidden" }}>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
            <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
              {getTranslation(translations, "generic.labels.caption")}
            </Typography>
            <IconButton
              color="primary"
              onClick={() => {
                setSearchTags([]);
                handleCloseLabels();
              }}
              size="small"
            >
              <FilterListOffIcon />
            </IconButton>
          </Box>
          <FormGroup>
            <FormControlLabel
              control={
                <Checkbox
                  checked={completeLabel}
                  onChange={() => {
                    setCompleteLabel((currentCompleteLabel: boolean) => {
                      localStorage.setItem(
                        CONSTANTS.STORE_LABEL_COMPLETE_MODE,
                        `${!currentCompleteLabel}`
                      );
                      return !currentCompleteLabel;
                    });
                  }}
                />
              }
              label={getTranslation(translations, "labels_view.tooltip")}
            />
          </FormGroup>
          {filteredTags.length > 0 && (
            <>
              <Box sx={{ borderTop: '1px solid', borderColor: 'divider', my: 1 }} />
              <Typography variant="subtitle2" sx={{ fontWeight: "bold", mb: 1 }}>
                {getTranslation(translations, "generic.filter.by.tag")}
              </Typography>
              <FormGroup>
                {displayedTags.map((tag) => (
                  <FormControlLabel
                    key={tag.idtag}
                    control={
                      <Checkbox
                        checked={searchTags.includes(tag.idtag)}
                        onChange={() => handleLabelCheckboxChange(tag.idtag)}
                      />
                    }
                    label={
                      <Labels
                        labelCompleteMode
                        labels={[{ id: tag.idtag, name: tag.tag, css: tag.color }]}
                      />
                    }
                  />
                ))}
              </FormGroup>
              {filteredTags.length > 5 && (
                <Button
                  size="small"
                  onClick={() => setShowAllTags(!showAllTags)}
                  sx={{ mt: 1, alignSelf: 'flex-start' }}
                >
                  {showAllTags
                    ? getTranslation(translations, "generic.showless")
                    : getTranslation(translations, "generic.showall")}
                </Button>
              )}
            </>
          )}
        </Box>
      </Menu>
      <Menu
        anchorEl={prioritiesMenuAnchorEl}
        open={Boolean(prioritiesMenuAnchorEl)}
        onClose={handleClosePriorities}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
      >
        <Box p={2} component={Stack} sx={{ minWidth: 200 }}>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
            <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
              {getTranslation(translations, "generic.priorities.caption")}
            </Typography>
            <IconButton
              color="primary"
              onClick={() => {
                setSelectedPriorities([]);
                handleClosePriorities();
              }}
              size="small"
            >
              <FilterListOffIcon />
            </IconButton>
          </Box>
          <FormGroup>
            {priorities.map((priority) => (
              <FormControlLabel
                key={priority.name}
                control={
                  <Checkbox
                    checked={selectedPriorities.includes(priority.name)}
                    onChange={() => handlePriorityCheckboxChange(priority.name)}
                  />
                }
                label={priority.name}
              />
            ))}
          </FormGroup>
        </Box>
      </Menu>
    </Stack>
  );
};

export default MyActivitiesFuncBar;
